import React, { useMemo, useState } from "react";
import {
  Button,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  Add,
  BlockOutlined,
  Search,
  TuneOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import AppSelectField from "../../lib/components/AppSelectField";
import CustomPagination from "../../lib/components/AppPagination";
import PreloaderTable from "../../lib/components/PreloaderTable";
import EmptyState from "../../lib/components/EmptyState";
import marketplaceService from "../../service/marketplaceService";
import organizationservice from "../../service/organizationservice";
import pathnames from "../../constants/pathnames";
import useDebounce from "../../hooks/useDebounce";
import GrantAddonModal from "./components/GrantAddonModal";
import UpdateQuantityModal from "./components/UpdateQuantityModal";
import RevokeAddonModal from "./components/RevokeAddonModal";
import MarketplaceStatusBadge from "./components/MarketplaceStatusBadge";
import {
  ADDON_SOURCE_OPTIONS,
  ADDON_STATUS_OPTIONS,
} from "./utils";
import "./marketplace.scss";

/**
 * Support / sales toolset (B5–B8):
 * - Audit trail of all purchases & grants
 * - Org entitlements inspector
 * - Manual grant, quantity adjust, revoke
 */
const MarketplacePayments = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = new URLSearchParams(window.location.search);

  const tab = searchParams.get("tab") || "audit";
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 20);
  const organizationId = searchParams.get("organizationId") || "";
  const status = searchParams.get("status") || "ALL";
  const source = searchParams.get("source") || "ALL";
  const orgKeyword = searchParams.get("orgKeyword") || "";
  const rule = searchParams.get("rule") || "";

  const debouncedOrgKeyword = useDebounce(orgKeyword, 400);

  const [grantOpen, setGrantOpen] = useState(false);
  const [quantityAddon, setQuantityAddon] = useState(null);
  const [revokeAddon, setRevokeAddon] = useState(null);

  const orgsQuery = useQuery({
    retry: false,
    enabled: Boolean(debouncedOrgKeyword),
    queryKey: ["marketplace-payment-orgs", debouncedOrgKeyword],
    queryFn: () =>
      organizationservice.get({ keyword: debouncedOrgKeyword, page: 1, limit: 20 }),
  });

  const orgOptions = useMemo(() => {
    const payload = orgsQuery.data?.data?.data ?? orgsQuery.data?.data ?? {};
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.docs)
        ? payload.docs
        : Array.isArray(payload?.organizations)
          ? payload.organizations
          : [];

    return list.map((org) => ({
      value: String(org.id || org._id),
      label: org.name || org.businessName || org.email || String(org.id || org._id),
    }));
  }, [orgsQuery.data]);

  const catalogQuery = useQuery({
    retry: false,
    queryKey: ["marketplace-items-catalog"],
    queryFn: () => marketplaceService.getItems({ page: 1, limit: 200 }),
  });

  const rulesQuery = useQuery({
    retry: false,
    enabled: tab === "entitlements",
    queryKey: ["marketplace-rules"],
    queryFn: () => marketplaceService.getRules(),
  });

  const addonsQuery = useQuery({
    retry: false,
    enabled: tab === "audit",
    queryKey: ["marketplace-addons", page, limit, organizationId, status, source],
    queryFn: () =>
      marketplaceService.getAddons({
        page,
        limit,
        organizationId: organizationId || undefined,
        status: status !== "ALL" ? status : undefined,
        source: source !== "ALL" ? source : undefined,
      }),
  });

  const entitlementsQuery = useQuery({
    retry: false,
    enabled: tab === "entitlements" && Boolean(organizationId),
    queryKey: ["marketplace-entitlements", organizationId, page, limit, rule],
    queryFn: () =>
      marketplaceService.getOrganizationEntitlements(organizationId, {
        page,
        limit,
        rule: rule || undefined,
      }),
  });

  const ruleOptions = useMemo(
    () => [
      { value: "", label: "All Rules" },
      ...(rulesQuery.data || []).map((r) => ({
        value: r.rule,
        label: r.label,
      })),
    ],
    [rulesQuery.data]
  );

  const auditRows = addonsQuery.data?.docs || [];
  const entitlementRows = entitlementsQuery.data?.docs || [];
  const totalPages =
    tab === "audit"
      ? addonsQuery.data?.totalPages || 1
      : entitlementsQuery.data?.totalPages || 1;

  const handleChange = (event, key) => {
    const value = event?.target ? event.target.value : event;
    const params = new URLSearchParams(window.location.search);
    if (value === "" || value == null || value === "ALL") params.delete(key);
    else params.set(key, value);
    if (key !== "page") params.set("page", "1");
    navigate(`${pathnames.MARKETPLACE_PAYMENTS}?${params.toString()}`);
  };

  const handleTabChange = (_, next) => {
    const params = new URLSearchParams();
    params.set("tab", next);
    if (organizationId) params.set("organizationId", organizationId);
    navigate(`${pathnames.MARKETPLACE_PAYMENTS}?${params.toString()}`);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["marketplace-addons"] });
    queryClient.invalidateQueries({ queryKey: ["marketplace-entitlements"] });
  };

  const startIndex = (Math.max(1, page) - 1) * Math.max(1, limit);

  const isCapacity = (row) => {
    const kind = String(row.kind || row.type || "").toUpperCase();
    return (
      kind === "LIMIT" ||
      row.quantity != null ||
      row.grantedQuantity != null
    );
  };

  return (
    <AppLayout>
      <Header
        title="Marketplace Payments & Grants"
        description="Audit every purchase/grant, inspect org entitlements, and run support actions (grant, quantity, revoke)."
      />
      <div className="p-3 bg-light marketplace-page" style={{ minHeight: "100%" }}>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab value="audit" label="Purchases & Grants" />
            <Tab value="entitlements" label="Org Entitlements" />
          </Tabs>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setGrantOpen(true)}
          >
            Grant Add-on
          </Button>
        </div>

        <div className="marketplace-filter-bar mb-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-4">
              <AppTextField
                placeholder="Search businesses..."
                value={orgKeyword}
                onChange={(e) => handleChange(e, "orgKeyword")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="text-secondary" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className="col-12 col-md-3">
              <AppSelectField
                value={organizationId}
                onChange={(e) => handleChange(e, "organizationId")}
                isLoading={orgsQuery.isFetching}
                items={[
                  { value: "", label: "All organizations" },
                  ...orgOptions,
                ]}
              />
            </div>
            {tab === "audit" ? (
              <>
                <div className="col-6 col-md-2">
                  <AppSelectField
                    value={status}
                    onChange={(e) => handleChange(e, "status")}
                    items={ADDON_STATUS_OPTIONS}
                  />
                </div>
                <div className="col-6 col-md-2">
                  <AppSelectField
                    value={source}
                    onChange={(e) => handleChange(e, "source")}
                    items={ADDON_SOURCE_OPTIONS}
                  />
                </div>
              </>
            ) : (
              <div className="col-12 col-md-3">
                <AppSelectField
                  value={rule}
                  onChange={(e) => handleChange(e, "rule")}
                  items={ruleOptions}
                />
              </div>
            )}
            <div className="col-12 col-md-1 text-md-end">
              <Button
                variant="text"
                onClick={() =>
                  navigate(`${pathnames.MARKETPLACE_PAYMENTS}?tab=${tab}`)
                }
              >
                Reset
              </Button>
            </div>
          </div>
        </div>

        <div className="marketplace-table-wrap">
          {tab === "audit" ? (
            addonsQuery.isPending || addonsQuery.isFetching ? (
              <PreloaderTable isLoading columns={7} rows={8} />
            ) : !auditRows.length ? (
              <div className="p-4">
                <EmptyState isEmpty message="No purchases or grants match your filters." />
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="marketplace-table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={64}>S/N</TableCell>
                      <TableCell>ORGANIZATION</TableCell>
                      <TableCell>ADD-ON</TableCell>
                      <TableCell>SOURCE</TableCell>
                      <TableCell>QTY</TableCell>
                      <TableCell>STATUS</TableCell>
                      <TableCell align="right">ACTIONS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditRows.map((row, index) => {
                      const id = row.id || row._id;
                      return (
                        <TableRow key={id || index} hover>
                          <TableCell className="text-secondary">
                            {startIndex + index + 1}
                          </TableCell>
                          <TableCell>
                            <div className="fw-medium">
                              {row.organizationName ||
                                row.organization?.name ||
                                row.organizationId ||
                                "—"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="fw-medium">
                              {row.name || row.itemName || row.itemCode || "—"}
                            </div>
                            <div className="text-secondary fs-xs">
                              {row.itemCode || row.code}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="marketplace-badge is-category">
                              {row.source || (row.grantedBy ? "GRANT" : "PURCHASE")}
                            </span>
                          </TableCell>
                          <TableCell>
                            {row.quantity ?? row.grantedQuantity ?? "—"}
                          </TableCell>
                          <TableCell>
                            <MarketplaceStatusBadge status={row.status || "ACTIVE"} />
                          </TableCell>
                          <TableCell align="right">
                            <div className="d-inline-flex">
                              {isCapacity(row) ? (
                                <Tooltip title="Update quantity">
                                  <IconButton
                                    size="small"
                                    onClick={() => setQuantityAddon(row)}
                                  >
                                    <TuneOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              <Tooltip title="Revoke">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setRevokeAddon(row)}
                                >
                                  <BlockOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          ) : !organizationId ? (
            <div className="p-4">
              <EmptyState
                isEmpty
                message="Select an organization to inspect entitlements (why can't this customer do X)."
              />
            </div>
          ) : entitlementsQuery.isPending || entitlementsQuery.isFetching ? (
            <PreloaderTable isLoading columns={6} rows={8} />
          ) : !entitlementRows.length ? (
            <div className="p-4">
              <EmptyState isEmpty message="No entitlements found for this organization." />
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="marketplace-table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={64}>S/N</TableCell>
                    <TableCell>ADD-ON / RULE</TableCell>
                    <TableCell>TYPE</TableCell>
                    <TableCell>VALUE</TableCell>
                    <TableCell>STATUS</TableCell>
                    <TableCell align="right">ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entitlementRows.map((row, index) => {
                    const id = row.id || row._id || row.addonId;
                    const name =
                      row.name ||
                      row.itemName ||
                      row.itemCode ||
                      row.rule ||
                      "Entitlement";
                    return (
                      <TableRow key={id || `${name}-${index}`} hover>
                        <TableCell className="text-secondary">
                          {startIndex + index + 1}
                        </TableCell>
                        <TableCell>
                          <div className="fw-medium">{name}</div>
                          {row.rule ? (
                            <div className="text-secondary fs-xs">{row.rule}</div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <span className="marketplace-badge is-category">
                            {row.kind || row.type || "—"}
                          </span>
                        </TableCell>
                        <TableCell>
                          {row.unlimited
                            ? "Unlimited"
                            : row.quantity ?? row.grantedQuantity ?? row.value ?? "—"}
                        </TableCell>
                        <TableCell>
                          <MarketplaceStatusBadge
                            status={
                              row.status || (row.active === false ? "REVOKED" : "ACTIVE")
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <div className="d-inline-flex">
                            {isCapacity(row) ? (
                              <Tooltip title="Update quantity">
                                <IconButton
                                  size="small"
                                  onClick={() => setQuantityAddon(row)}
                                >
                                  <TuneOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            {id ? (
                              <Tooltip title="Revoke">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setRevokeAddon(row)}
                                >
                                  <BlockOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <VisibilityOutlined
                                className="text-secondary"
                                fontSize="small"
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {(tab === "audit" || organizationId) && (
            <div className="px-3">
              <CustomPagination
                page={page}
                count={totalPages}
                limit={limit}
                limitOptions={[10, 20, 50]}
                onPageChange={(_, next) => handleChange(String(next), "page")}
                onLimitChange={(next) => handleChange(String(next), "limit")}
              />
            </div>
          )}
        </div>
      </div>

      <GrantAddonModal
        isOpen={grantOpen}
        onClose={() => setGrantOpen(false)}
        onSuccess={invalidate}
        catalogItems={catalogQuery.data?.docs || []}
      />

      <UpdateQuantityModal
        isOpen={Boolean(quantityAddon)}
        onClose={() => setQuantityAddon(null)}
        onSuccess={invalidate}
        addon={quantityAddon}
      />

      <RevokeAddonModal
        isOpen={Boolean(revokeAddon)}
        onClose={() => setRevokeAddon(null)}
        onSuccess={invalidate}
        addon={revokeAddon}
      />
    </AppLayout>
  );
};

export default MarketplacePayments;
